import fileService from './fileService';

// Map HestiaV2 door metadata to drawing generator format
const mapDoorDataToDrawingGenerator = (doorMetadata, projectInfo, userProfile) => {
  // Map door types from HestiaV2 to drawing generator format
  const doorTypeMapping = {
    'door_60mm': 'door_60mm',
    'door_meranti': 'door_meranti', 
    'door_cottage': 'door_cottage',
    'door_18deg': 'door_18deg',
    'door_60mm_inward': 'door_60mm_inward'
  };

  // Map threshold types from HestiaV2 to drawing generator
  const thresholdMapping = {
    'Pine': 'pine',
    'Oak': 'oak',
    'ACCOYA': 'oak', // Map ACCOYA to oak as drawing generator doesn't have ACCOYA
    'Alu w holes': 'alu',
    'Alu-no holes': 'alu',
    '': '' // No threshold
  };

  // Map lock types from HestiaV2 to drawing generator
  // Only 3PL locks map to '3pl', all others map to 'abloy' (ASSA)
  const lockMapping = {
    'none': '',
    '565 analoge': 'abloy',
    '8765 analoge': 'abloy',
    '2150-3PL analoge': '3pl',  // Only 3PL
    'ASSA 8765': 'abloy',
    'Assa': 'abloy',
    '3-Point Lock': '3pl'
  };

  // Extract project order number (project_number field)
  // Use project_number if available, otherwise fall back to name
  // Remove CO- prefix if present, as it's already hardcoded in the drawing generator
  let orderNumber = projectInfo?.project_number || projectInfo?.name || 'AUTO-GEN';
  
  // Strip CO- prefix if it exists (e.g., "CO-317" becomes "317")
  if (orderNumber.startsWith('CO-')) {
    orderNumber = orderNumber.replace('CO-', '');
  }
  
  // Add door number if available (e.g., "317-1", "317-2")
  if (doorMetadata.doorNumber) {
    orderNumber = `${orderNumber}-${doorMetadata.doorNumber}`;
  }
  
  console.log('🔍 Drawing Generator Mapping Debug:');
  console.log('  - projectInfo:', projectInfo);
  console.log('  - projectInfo.project_number:', projectInfo?.project_number);
  console.log('  - projectInfo.name:', projectInfo?.name);
  console.log('  - doorMetadata.doorNumber:', doorMetadata.doorNumber);
  console.log('  - userProfile:', userProfile);
  console.log('  - userProfile.full_name:', userProfile?.full_name);
  console.log('  - userProfile.username:', userProfile?.username);

  // Get creator name from user profile (full_name or username)
  const creator = userProfile?.full_name || userProfile?.username || 'HestiaV2 User';
  
  console.log('  - Final orderNumber:', orderNumber);
  console.log('  - Final creator:', creator);

  // Map frame width based on door type
  // 92mm: cottage, meranti, 18deg
  // 105mm: 60mm outward
  // 115mm: 60mm inward
  let frameWidth = '92mm'; // Default
  if (doorMetadata.doorName) {
    const doorName = doorMetadata.doorName.toLowerCase();
    
    // 60mm inward = 115mm
    if (doorName.includes('inward') || doorName === '60i' || doorName === 'door_60mm_inward') {
      frameWidth = '115mm';
    }
    // 60mm outward = 105mm
    else if (doorName.includes('60mm') || doorName === '60o' || doorName === 'door_60mm') {
      frameWidth = '105mm';
    }
    // Meranti = 92mm
    else if (doorName.includes('meranti') || doorName === 'mer' || doorName === 'door_meranti') {
      frameWidth = '92mm';
    }
    // Cottage = 92mm
    else if (doorName.includes('cottage') || doorName === 'cot' || doorName === 'door_cottage') {
      frameWidth = '92mm';
    }
    // 18deg = 92mm
    else if (doorName.includes('18deg') || doorName === '18d' || doorName === 'door_18deg') {
      frameWidth = '92mm';
    }
  }

  // Map hinge count to drawing generator format (2hinge, 3hinge)
  // Only support 2 or 3 hinges for now
  let hinges = '2hinge'; // Default
  if (doorMetadata.hingeCount) {
    const count = doorMetadata.hingeCount.toString();
    if (count === '2') {
      hinges = '2hinge';
    } else if (count === '3') {
      hinges = '3hinge';
    } else {
      // If 4 or other, default to 2
      hinges = '2hinge';
    }
  }

  return {
    // Basic door info
    width: doorMetadata.width || 988,
    height: doorMetadata.height || 2078,
    orderNumber: orderNumber,
    creator: creator,
    
    // Door specifications
    product: doorTypeMapping[doorMetadata.doorName] || 'door_60mm',
    doorType: doorMetadata.doorName || 'door_60mm',
    lockType: lockMapping[doorMetadata.lockType] || 'abloy',
    threshold: thresholdMapping[doorMetadata.thresholdType] || 'oak',
    hinges: hinges,
    handedness: doorMetadata.handedness || 'Left',
    
    // Features
    glass: doorMetadata.hasGlass || false,
    waternose: doorMetadata.waternose || false,
    kickplate: doorMetadata.kickplate || false,
    grooves: false, // Default value, not in current door metadata
    
    // Additional properties
    framewidth: frameWidth,
    doorsets: doorMetadata.amount || 1,
    
    // Glass properties (defaults if glass is enabled)
    glassWidth: 500,
    glassHeight: 750,
    glassOffsetLeft: 0,
    glassOffsetTop: 150
  };
};

// Generate drawing generator URL with parameters
const generateDrawingGeneratorUrl = (doorData) => {
  // Just use the same origin - much simpler!
  const baseUrl = '/drawinggenerator/index.html';
  const params = new URLSearchParams();
  
  // Add all door parameters to URL
  Object.entries(doorData).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value);
    }
  });
  
  return `${baseUrl}?${params.toString()}`;
};

export const drawingGeneratorService = {
  // Open drawing generator with door data
  async openDrawingGenerator(doorMetadata, projectInfo, userProfile) {
    try {
      const drawingData = mapDoorDataToDrawingGenerator(doorMetadata, projectInfo, userProfile);
      const generatorUrl = generateDrawingGeneratorUrl(drawingData);
      
      return {
        success: true,
        url: generatorUrl,
        data: drawingData
      };
    } catch (error) {
      console.error('Error preparing drawing generator:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Save generated PDF to door folder
  async savePdfToFolder(folderId, fileToSave, filename, doorMetadata, projectId) {
    try {
      console.log('📁 Saving file to folder:', { folderId, filename, fileType: fileToSave.type, projectId });
      
      // Upload to the door folder using existing file service
      // fileService.uploadFile expects (file, projectId, folderId)
      const uploadResult = await fileService.uploadFile(fileToSave, projectId, folderId);
      
      console.log('📤 Upload result:', uploadResult);
      
      // The fileService.uploadFile returns the file data directly, not a success object
      if (uploadResult && uploadResult.id) {
        return {
          success: true,
          fileId: uploadResult.id,
          filename: uploadResult.filename,
          message: 'Drawing specifications saved successfully'
        };
      } else {
        console.error('❌ Upload result missing or invalid:', uploadResult);
        throw new Error('Upload failed - invalid response from file service');
      }
    } catch (error) {
      console.error('Error saving PDF to folder:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Convert drawing generator data back to HestiaV2 format (for updates)
  mapDrawingGeneratorDataToDoor(generatorData) {
    const doorTypeReverseMapping = {
      'door_60mm': 'door_60mm',
      'door_meranti': 'door_meranti',
      'door_cottage': 'door_cottage', 
      'door_18deg': 'door_18deg',
      'door_60mm_inward': 'door_60mm_inward'
    };

    const thresholdReverseMapping = {
      'alu': 'alu',
      'oak': 'oak',
      '': ''
    };

    const lockReverseMapping = {
      'Assa': 'Assa',
      '3-Point Lock': '3-Point Lock'
    };

    return {
      width: parseFloat(generatorData.width) || 988,
      height: parseFloat(generatorData.height) || 2078,
      doorName: doorTypeReverseMapping[generatorData.product] || 'door_60mm',
      hasGlass: generatorData.glass === true || generatorData.glass === 'true',
      hingeType: generatorData.hinges || '2',
      lockType: lockReverseMapping[generatorData.lockType] || 'Assa',
      amount: parseInt(generatorData.doorsets) || 1,
      waternose: generatorData.waternose === true || generatorData.waternose === 'true',
      thresholdType: thresholdReverseMapping[generatorData.threshold] || '',
      kickplate: generatorData.kickplate === true || generatorData.kickplate === 'true',
      handedness: generatorData.handedness || 'Left'
    };
  },

  // Validate drawing generator is accessible
  async validateDrawingGenerator() {
    // Skip validation - just assume it works to simplify things
    console.log('Skipping drawing generator validation for simplicity');
    return true;
  }
};

export default drawingGeneratorService;
